// public/js/features/assets/asset-detail-view.js
import { getAssetDetail, updateAsset, getUnassignedDevices, deleteAsset, updateAssetSlot, dockAssetSlot } from './api.js';
import { updateAssetInCache, getAssetsCache, removeAssetFromCache } from './assets-state.js';
import { staticMapHtml, osmTile, OSM_CREDIT } from '../../modules/map/static-preview.js';
import { loadTenants, loadTenantFieldConfig } from '../../modules/tenants.js';
import { can, canExport, getCurrentUser } from '../../modules/rbac.js';
import { renderPhotoGallery } from '../files/photo-gallery.js';
import { formatAssetLabel, formatTimeAgo, formatLocalTime } from '../../utils/asset-utils.js';
import { getTemplate, applyOrder, SURFACES } from '../display/template-store.js';
import { quickExport } from '../reports/api.js';
import { renderAssetChartsSection } from './asset-charts.js';
import { renderAssetTripsSection } from './asset-trips.js';
import { renderAssetShipmentsSection } from './asset-shipments.js';
import { renderStandModelPanel } from './stand-model-panel.js';
import { renderAssetConfigSection } from '../tracker-config/asset-config-section.js';
import { batteryRemainingPill, wireBatteryHelp } from '../tracker-config/battery-remaining-ui.js';
import { renderAssetDerivedSection } from './slot-derived-editor.js';
import { slotForDevice, slotDisplayLabel, deviceRole } from './slot-utils.js';
import { deviceTypeLabel, deviceImage } from '../devices/device-type.js';
import { manualHrefForDevice } from '../devices/branded-models.js';
import { showConfirm } from '../../modules/ui/confirm.js';
import { openRawUplinkModal } from './raw-uplinks-modal.js';
import { openRouteModal } from './route-modal.js';
import { resolveFieldLabels, DEFAULT_ASSET_FIELD_LABELS, customTextFields, fixedTextFields } from './field-labels.js';
import { loadAssetFields, visibleFields, effectiveFieldLabel, optionLabel } from './asset-fields.js';
import { escapeHtml } from '../../utils/escape.js';
import { ensureScopeFor } from '../../modules/tenant-scope.js';
import { renderAssetFilesSection, renderAssetPhotosSection } from '../files/asset-files-section.js';
import { featureEnabled } from '../../modules/features.js';
import { helpIcon, helpHover, initHelp } from '../../ui/help.js';
import { convertNumber, displayUnit } from '../../utils/units.js';

// tenantId → display name, resolved once per detail render so the read-only
// view shows the tenant name instead of the raw UUID.
let tenantNameById = {};
// Asset-owner tenant's custom field labels (label/reference/serial).
let currentFieldLabels = { ...DEFAULT_ASSET_FIELD_LABELS };
// Visible registry dropdown fields (type, engineType, …) for the asset's tenant,
// each: { fieldId, label, options:[{value,label}] }. Built per detail render.
let currentRegistryFields = [];
// Enabled per-tenant custom free-text fields (custom1/custom2) for this asset.
let currentCustomFields = [];
// The full field array currently rendered (so enterEditMode can find options).
let currentBasicFields = [];
// The asset currently shown — exitEditMode/save re-render from THIS, not from
// the assets-list cache (which may not contain the asset at all).
let currentAsset = null;
// The "Back to All Assets" callback from the page. Re-renders triggered INSIDE
// this view (after an edit / device save) must reuse it — passing a no-op there
// silently broke the back button after any edit.
let currentOnBack = null;

export async function renderAssetDetail(container, assetUUID, onBack) {
  // Keep the real back-callback so internal re-renders (below) can reuse it.
  currentOnBack = onBack;
  container.innerHTML = `
    <!-- Back button, then the title on its own line below - so a long asset name
         is never squeezed on mobile. Delete lives at the very bottom (danger zone). -->
    <div class="mb-4">
      <!-- Back on the left, the tenant control on the RIGHT of the same row.
           It used to be one more field in the list below; up here it costs no
           vertical space, and it is hidden entirely for users who may not
           re-home an asset, so the row simply collapses to the back button. -->
      <div class="mb-3 d-flex align-items-center gap-2 flex-wrap">
        <button id="btn-back-to-assets" class="btn btn-outline-secondary d-inline-flex align-items-center gap-2 text-nowrap">
          <i class="fa-solid fa-arrow-left"></i> Back to All Assets
        </button>
        <div id="detail-tenant-control" class="ms-auto"></div>
      </div>
      <h2 id="detail-header" class="h4 mb-0"></h2>
    </div>

    <div class="card shadow-sm">
      <div class="card-body">
        <h5 class="mb-3">Basic Information</h5>
        <!-- Photo, then map, then the fields. The gallery column only exists
             when the asset actually has photos; renderBasicFields() then drops
             the fields to a single column so the three sit side by side without
             the row wrapping. With no photo the fields keep two columns. -->
        <div class="row g-3" id="basic-row">
          <div class="col-lg-3 d-none" id="detail-photo-gallery"></div>
          <div class="col-lg-4" id="detail-static-map"></div>
          <div class="col-lg-8" id="basic-fields-col">
            <div id="basic-fields" class="row g-3"></div>
          </div>
        </div>

        <!-- 3D model of the engine stand type. Self-gating: renders nothing
             unless the asset has an engineStandType we hold a model for. -->
        <div id="asset-stand-model-section"></div>

        <!-- Sensor history charts -->
        <h5 class="mt-5 mb-3 d-flex align-items-center gap-2 flex-wrap">
          Sensor history
          <span class="btn-group btn-group-sm" id="detail-chart-ranges"></span>
          <button type="button" id="detail-chart-reload" class="btn btn-sm btn-outline-secondary" title="Reload sensor history">
            <i class="fa-solid fa-arrows-rotate"></i>
          </button>
          ${exportMenu('sensor-export')}
          <span id="detail-uplink-interval" class="small text-muted fw-normal ms-1"></span>
        </h5>
        <div id="sensor-charts-section"></div>

        <!-- Trips: derived from the positions the tracker reports -->
        <h5 class="mt-5 mb-3">Trips</h5>
        <div id="asset-trips-section" class="mb-4"></div>

        <!-- Shipments: what the asset was SUPPOSED to do, beside what it did -->
        <h5 class="mt-5 mb-3">Shipments</h5>
        <div id="asset-shipments-section" class="mb-4"></div>

        <!-- Photos + Documents: per-asset file store (docs/asset-files.md §8).
             The HEADINGS are inside the flag check too — without it an
             environment where the file store is off showed two empty sections. -->
        ${featureEnabled('assetFiles') ? `
        <h5 class="mt-5 mb-3">Photos</h5>
        <div id="asset-photos-section" class="mb-4"></div>

        <h5 class="mt-5 mb-3">Documents</h5>
        <div id="asset-files-section" class="mb-4"></div>` : ''}

        <!-- Paired Devices (multi-device) -->
        <h5 class="mt-5 mb-3">Paired Devices <span id="device-count-badge" class="badge bg-info"></span></h5>
        <div id="paired-devices-section" class="mb-4"></div>

        <!-- Tracker configuration (template inheritance + per-device config twin).
             Self-gates: renders nothing unless the user has manageDeviceConfig
             and the asset actually has paired trackers. See docs/tracker-config.md -->
        <div id="asset-config-section" class="mb-4"></div>

        <!-- Derived sensors (per-asset overrides of the tenant threshold rules).
             Self-gates: renders nothing unless the user has manageTenants and the
             tenant has rules (or the asset already overrides). See docs/derived-sensors.md -->
        <div id="asset-derived-section" class="mb-4"></div>

        <!-- Location & Metadata (read-only) + raw uplink log — kept at the very
             bottom: it is reference/debug detail, below the day-to-day sections. -->
        <h5 class="mt-5 mb-3 d-flex align-items-center gap-2 flex-wrap">Location &amp; Metadata
          <button type="button" id="btn-raw-uplinks" class="btn btn-sm btn-outline-secondary" title="View raw uplink log (debug)">
            <i class="fa-solid fa-terminal me-1"></i> Raw uplinks
          </button>
          ${exportMenu('raw-export')}
        </h5>
        <div id="readonly-section" class="row g-3 text-muted small"></div>
      </div>
    </div>

    <!-- Danger zone: destructive action kept at the very bottom, out of the way. -->
    <div class="d-flex justify-content-end mt-4">
      <button id="btn-delete-asset" class="btn btn-outline-danger d-inline-flex align-items-center gap-2">
        <i class="fa-solid fa-trash"></i> Delete asset
      </button>
    </div>
  `;

  // Always fetch the fresh detail: the assets-list cache carries the STORED
  // assignedDevices snapshot (frozen at assignment time), so paired devices'
  // lastSeen/battery would show stale, days-old values. The detail endpoint
  // joins devices live. Cache is only the offline/error fallback.
  let asset = await getAssetDetail(assetUUID).catch(() => null)
    || getAssetsCache().find(a => a.assetUUID === assetUUID);
  currentAsset = asset || null;
  if (!asset) {
    container.innerHTML = `<div class="alert alert-danger">Asset not found</div>`;
    return;
  }

  // Deep link outside the active tenant scope (alert mail, shared URL): the
  // scope AUTO-SWITCHES to this asset's tenant (docs/tenant-scope.md).
  try { ensureScopeFor(asset.tenantId); } catch (e) {}

  // Resolve tenant names (UUID → name) for the read-only display.
  // Use the white-label config endpoint (open to any member), NOT loadTenants,
  // which needs the listTenants permission — otherwise viewers/operators fall
  // back to default field labels instead of the tenant's custom ones.
  try {
    const tenants = await loadTenantFieldConfig();
    tenantNameById = Object.fromEntries(tenants.map(t => [t.tenantId, t.name || t.tenantId]));
    // The asset's owner tenant decides the field labels shown on this page.
    const ownerTenant = tenants.find(t => t.tenantId === asset.tenantId) || null;
    currentFieldLabels = resolveFieldLabels(ownerTenant);
    // Visible registry dropdown fields for this asset's tenant (label + options).
    try {
      const defs = await loadAssetFields();
      currentRegistryFields = visibleFields(defs, ownerTenant).map(f => ({
        fieldId: f.fieldId,
        label: effectiveFieldLabel(f, ownerTenant),
        options: f.options || [],
      }));
    } catch { currentRegistryFields = []; }
    currentCustomFields = [...customTextFields(ownerTenant), ...fixedTextFields(ownerTenant)].filter(f => f.enabled);
  } catch (e) {
    tenantNameById = {};
    currentFieldLabels = { ...DEFAULT_ASSET_FIELD_LABELS };
    currentRegistryFields = [];
    currentCustomFields = [];
  }

  // Header
  document.getElementById('detail-header').innerHTML = `
    ${escapeHtml(formatAssetLabel(asset))} <span class="badge bg-${asset.status === 'active' ? 'success' : 'warning'}">${escapeHtml(asset.status || 'active')}</span>
  `;

  document.getElementById('detail-header').dataset.assetUuid = assetUUID;

  // Static map (first column of Basic Information)
  renderStaticMap(asset);

  // Trips section (async, self-contained — no need to block the rest).
  renderAssetTripsSection(document.getElementById('asset-trips-section'), assetUUID);
  renderAssetShipmentsSection(document.getElementById('asset-shipments-section'), assetUUID);

  // 3D model of the stand type (async, self-gating — renders nothing when the
  // asset has no engineStandType or we hold no model for it).
  renderStandModelPanel(document.getElementById('asset-stand-model-section'), asset);

  // Photos + documents (async, self-gating on permission; never block the page).
  // Feature-flagged as well: without the flag the endpoints are not routed at
  // all, so rendering these would only produce sections full of failed requests.
  if (featureEnabled('assetFiles')) {
    // The containers only exist when the flag is on (see the template above).
    renderAssetFilesSection(document.getElementById('asset-files-section'), assetUUID);

    // The gallery at the top of Basic Information and the Photos section below
    // show the same photos, so a change in either has to reach the other.
    // Deliberately one-way in each direction to avoid a refresh loop: the
    // section notifies the gallery, and the gallery re-renders the section.
    const mountGallery = async () => {
      const host = document.getElementById('detail-photo-gallery');
      if (!host) return;
      const shown = await renderPhotoGallery(host, assetUUID, { onChanged: drawPhotoSection });
      // Only make room for the gallery once it actually has something to show.
      if (shown) applyPhotoLayout();
    };
    const drawPhotoSection = () => renderAssetPhotosSection(
      document.getElementById('asset-photos-section'), assetUUID, { onChanged: mountGallery },
    );
    drawPhotoSection();
    mountGallery();
  }

  // Tracker configuration section (async, self-gating on permission + trackers).
  if (featureEnabled('trackerConfig')) {
    renderAssetConfigSection(document.getElementById('asset-config-section'), assetUUID);
  }

  // Derived-sensor overrides section (async, self-gating on permission + rules).
  renderAssetDerivedSection(document.getElementById('asset-derived-section'), asset);

  // Read-only Location/Metadata
  const readonlyHTML = `
    <div class="col-12"><strong>Last Position:</strong> ${asset.lastLat || '—'}, ${asset.lastLon || '—'} 
      <a href="https://www.google.com/maps?q=${asset.lastLat},${asset.lastLon}" target="_blank" rel="noopener noreferrer" class="text-info">Open Maps</a>
    </div>
    <div class="col-12"><strong>Last Updated:</strong> ${asset.lastUpdated ? new Date(asset.lastUpdated).toLocaleString('en-GB') : '—'}</div>
    ${asset.lastFixAt && asset.lastUpdated && (new Date(asset.lastUpdated) - new Date(asset.lastFixAt)) > 2 * 3600 * 1000
      ? `<div class="col-12 text-warning"><i class="fa-solid fa-triangle-exclamation me-1"></i><strong>Last position fix:</strong> ${new Date(asset.lastFixAt).toLocaleString('en-GB')} (${formatTimeAgo(asset.lastFixAt)}) - the device has reported since, but without locating itself; the position above is its last known one.</div>`
      : ''}
    <div class="col-12"><strong>Created:</strong> ${asset.createdAt ? new Date(asset.createdAt).toLocaleString('en-GB') : '—'}</div>
  `;
  document.getElementById('readonly-section').innerHTML = readonlyHTML;

  // Basic editable fields (unchanged)
  renderBasicFields(asset);

  // Sensor history charts (async; renders its own loading state)
  renderAssetChartsSection(
    document.getElementById('sensor-charts-section'),
    document.getElementById('detail-chart-ranges'),
    assetUUID,
    document.getElementById('detail-chart-reload'),
    asset
  ).catch(console.error);

  // Inline data exports (sensor readings + raw uplinks) via the reporting
  // framework — only present when canExport() rendered the menus.
  wireExportMenus(assetUUID, asset);

  // Multi-device section (completely replaces the old renderDeviceSection)
  await renderPairedDevicesSection(asset, assetUUID);

  // Raw uplink log (debug)
  document.getElementById('btn-raw-uplinks').onclick = () => openRawUplinkModal(assetUUID, formatAssetLabel(asset));

  // Back & Delete
  document.getElementById('btn-back-to-assets').onclick = onBack;
  document.getElementById('btn-delete-asset').onclick = async () => {
    if (!confirm(`Delete asset "${formatAssetLabel(asset)}"? This cannot be undone.`)) return;
  
    const btn = document.getElementById('btn-delete-asset');
    btn.disabled = true;
    btn.innerHTML = `<span class="spinner-border spinner-border-sm me-2"></span>Deleting...`;
  
    try {
      await deleteAsset(asset.assetUUID);
      removeAssetFromCache(asset.assetUUID);      // remove from cache → table stays correct
      window.showToast(`Asset "${formatAssetLabel(asset)}" deleted`, 'success');
      onBack();                                    // back to the table (cache-first, no reload)
    } catch (e) {
      console.error('[Delete]', e);
      window.showToast('Delete failed — please try again', 'danger');
      btn.disabled = false;
      btn.innerHTML = `<i class="fa-solid fa-trash"></i> Delete`;
    }
  };
}

// Build a Google Static Map for the asset's last position, shown in the first
// column of Basic Information. Falls back to a link placeholder when there is
// no position or the static image fails to load.
function renderStaticMap(asset) {
  const el = document.getElementById('detail-static-map');
  if (!el) return;
  el.style.position = 'relative';

  const lat = parseFloat(asset.lastLat);
  const lon = parseFloat(asset.lastLon);
  const reloadBtn = `<button type="button" class="asset-map-reload js-map-reload" title="Reload position"><i class="fa-solid fa-arrows-rotate"></i></button>`;

  let content;
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    content = `<div class="border rounded d-flex align-items-center justify-content-center text-muted small" style="height:100%;min-height:200px;cursor:pointer;" data-map-open><span><i class="fa-solid fa-map-location-dot me-1"></i> No position yet — show on map</span></div>`;
  } else {
    // Built from the raster tiles we already use, not Google Static Maps -
    // whose terms forbid storing the image and forbid use alongside a
    // non-Google map, which is every other map in this app (docs/maps.md).
    // It also needs no key, so there is no "no key, no preview" branch any more.
    content = `<a href="#map" title="Show on map" class="d-block h-100 rounded border overflow-hidden" data-map-open>`
      + staticMapHtml({ lat, lon, zoom: 14, width: 420, height: 420,
                        tileUrl: osmTile, attribution: OSM_CREDIT,
                        alt: 'Asset location map' })
      + `</a>`;
  }

  // Wrap so the reload button is inset from the IMAGE (not the stretched
  // column) — equal gap from top and right.
  el.innerHTML = `<div class="asset-map-wrap" style="position:relative;display:block;">${content}${reloadBtn}</div>`;

  // Clicking the mini-map opens the full-screen historical route modal.
  const openRoute = (e) => { e?.preventDefault(); openRouteModal(asset); };
  el.querySelector('[data-map-open]')?.addEventListener('click', openRoute);

  const img = el.querySelector('img');
  if (img) img.addEventListener('error', () => {
    const a = el.querySelector('[data-map-open]');
    if (a) a.innerHTML = `<div class="border rounded p-3 text-muted small d-flex align-items-center justify-content-center w-100" style="min-height:200px;"><span><i class="fa-solid fa-map-location-dot me-1"></i> Show on map</span></div>`;
  });

  // Subtle reload: re-fetch the asset's latest position and redraw the map.
  el.querySelector('.js-map-reload')?.addEventListener('click', async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const btn = e.currentTarget;
    const icon = btn.querySelector('i');
    btn.disabled = true;
    icon?.classList.add('fa-spin');
    try {
      const fresh = await getAssetDetail(asset.assetUUID);
      if (fresh) {
        updateAssetInCache(fresh);
        renderStaticMap(fresh); // rebuilds content + button + listeners
      }
    } catch (err) {
      window.showToast?.('Could not reload position', 'danger');
      btn.disabled = false;
      icon?.classList.remove('fa-spin');
    }
  });
}

function renderBasicFields(asset) {
  const container = document.getElementById('basic-fields');
  const tenantDisplay = tenantNameById[asset.tenantId] || asset.tenantName || asset.tenantId || '';
  const L = currentFieldLabels; // per-tenant custom field labels (with defaults)
  const fields = [
    { key: 'label', label: L.label, type: 'text', value: asset.label || '' },
    { key: 'reference', label: L.reference, type: 'text', value: asset.reference || '' },
    { key: 'serial', label: L.serial, type: 'text', value: asset.serial || '' },
  ];
  // Registry dropdown fields (Asset type, Engine type, …) — only those visible
  // for this tenant; options + label come from the registry. select2 in edit.
  for (const rf of currentRegistryFields) {
    fields.push({
      key: rf.fieldId, label: rf.label, type: 'select', registry: true,
      options: rf.options, value: asset[rf.fieldId] || '',
      display: optionLabel(rf, asset[rf.fieldId]) || '—',
    });
  }
  // Optional per-tenant custom free-text fields (custom1/custom2), when enabled.
  for (const cf of currentCustomFields) {
    fields.push({ key: cf.key, label: cf.label, type: 'text', value: asset[cf.key] || '' });
  }
  fields.push({ key: 'status', label: 'Status', type: 'select', options: [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }], value: asset.status || 'active' });
  fields.push({ key: 'tenantId', label: 'Tenant', type: 'select', value: asset.tenantId || '', display: tenantDisplay });

  // tenantId stays in currentBasicFields (enterEditMode reads its type from
  // there) but is rendered in the page header instead of this grid.
  currentBasicFields = fields;
  const fieldRow = (field, extraClass) => {
    const shown = (field.display ?? field.value) || '—';
    return `
      <div class="${extraClass} field-row" data-field="${field.key}">
        <label class="form-label">${escapeHtml(field.label)}</label>
        <div class="input-group">
          <span id="display-${field.key}" class="form-control d-flex align-items-center${field.key === 'tenantId' ? ' tenant-name' : ''}">${escapeHtml(shown)}</span>
          <button class="btn btn-outline-secondary edit-btn" data-field="${field.key}">
            <i class="fa-solid fa-pencil"></i>
          </button>
        </div>
      </div>`;
  };

  // Which rows show and in which order: Settings > Display > "Asset detail
  // fields" (docs/asset-display-templates.md). Rows the template never heard of
  // keep their built-in place at the end; without a template nothing changes.
  let shownFields = fields.filter(f => f.key !== 'tenantId');
  try {
    const tpl = getTemplate(SURFACES.assetDetail);
    if (tpl?.fields?.length) {
      const byKey = new Map(shownFields.map(f => [f.key, f]));
      const ordered = applyOrder(shownFields.map((f, i) => ({ key: f.key, defaultOrder: (i + 1) * 10 })), tpl, 'fields')
        .map(o => byKey.get(o.key)).filter(Boolean);
      if (ordered.length) shownFields = ordered;
    }
  } catch (e) { /* template store not ready -> built-in order */ }

  container.innerHTML = shownFields
    .map(f => fieldRow(f, 'col-md-6')).join('');

  // The tenant control in the header. Only for the roles the SERVER lets change
  // it (auth.mjs: superadmin | admin | platformSupport) — anyone else would get
  // a pencil that always fails, so they get nothing at all and the row collapses
  // to just the back button.
  const host = document.getElementById('detail-tenant-control');
  if (host) {
    const groups = getCurrentUser()?.groups || [];
    const mayMove = groups.some(g => ['superadmin', 'admin', 'platformSupport'].includes(g));
    const tenantField = fields.find(f => f.key === 'tenantId');
    host.innerHTML = (mayMove && tenantField)
      ? `<div class="field-row d-flex align-items-center gap-2" data-field="tenantId" style="min-width:240px;">
           <label class="form-label mb-0 small text-muted text-nowrap">${escapeHtml(tenantField.label)}</label>
           <div class="input-group input-group-sm">
             <span id="display-tenantId" class="form-control d-flex align-items-center tenant-name">${escapeHtml(tenantField.display || tenantField.value || '—')}</span>
             <button class="btn btn-outline-secondary edit-btn" data-field="tenantId" title="Move this asset to another tenant">
               <i class="fa-solid fa-pencil"></i>
             </button>
           </div>
         </div>`
      : '';
    host.querySelectorAll('.edit-btn').forEach(btn => {
      btn.addEventListener('click', () => enterEditMode(btn.dataset.field, asset));
    });
  }

  // Attach pencil listeners
  container.querySelectorAll('.edit-btn').forEach(btn => {
    btn.addEventListener('click', () => enterEditMode(btn.dataset.field, asset));
  });
}

/**
 * Make room for the gallery once we know the asset has photos: the fields drop
 * from two columns to one so photo, map and fields sit side by side instead of
 * wrapping. Called only when the gallery actually rendered something.
 */
function applyPhotoLayout() {
  document.getElementById('detail-photo-gallery')?.classList.remove('d-none');
  const col = document.getElementById('basic-fields-col');
  if (col) { col.classList.remove('col-lg-8'); col.classList.add('col-lg-5'); }
  document.querySelectorAll('#basic-fields > .field-row').forEach(el => {
    el.classList.remove('col-md-6');
    el.classList.add('col-12');
  });
}

async function enterEditMode(fieldKey, originalAsset) {
  const row = document.querySelector(`.field-row[data-field="${fieldKey}"]`);
  const field = currentBasicFields.find(f => f.key === fieldKey) || { key: fieldKey, type: 'text' };
  const originalValue = originalAsset[fieldKey] ?? '';
  const esc = (s) => String(s).replace(/"/g, '&quot;');

  let inputHTML = '';
  let useSelect2 = false;
  if (field.type === 'select') {
    const sel = (val) => val === originalValue ? ' selected' : '';
    let options = '';
    if (fieldKey === 'tenantId') {
      const tenants = await loadTenants();
      options = tenants.map(t => `<option value="${esc(t.tenantId)}"${sel(t.tenantId)}>${escapeHtml(t.name || t.tenantId)}</option>`).join('');
    } else {
      // Registry / fixed selects use [{value,label}]. Registry selects may be
      // optional, so offer a blank choice; many options → select2.
      if (field.registry) {
        useSelect2 = true;
        options = `<option value=""${sel('') }>— none —</option>`;
      }
      options += (field.options || []).map(o => `<option value="${esc(o.value)}"${sel(o.value)}>${escapeHtml(o.label || o.value)}</option>`).join('');
    }
    inputHTML = `<select id="edit-input-${fieldKey}" class="form-select${fieldKey === 'tenantId' ? ' tenant-blur' : ''}" style="flex:1 1 auto;">${options}</select>`;
  } else {
    inputHTML = `<input id="edit-input-${fieldKey}" type="text" class="form-control" value="${esc(originalValue)}">`;
  }

  const editHTML = `
    <div class="input-group">
      ${inputHTML}
      <button class="btn btn-success save-btn" data-field="${fieldKey}"><i class="fa-solid fa-check"></i></button>
      <button class="btn btn-outline-secondary cancel-btn" data-field="${fieldKey}"><i class="fa-solid fa-times"></i></button>
    </div>`;

  row.querySelector('.input-group').innerHTML = editHTML;
  if (useSelect2 && window.$ && $.fn.select2) {
    $(`#edit-input-${fieldKey}`).select2({ width: '100%', allowClear: false });
  }

  // Save
  row.querySelector('.save-btn').addEventListener('click', async () => {
    const input = document.getElementById(`edit-input-${fieldKey}`);
    const newValue = input.value.trim();
    if (newValue === originalValue) {
      exitEditMode(fieldKey, originalValue);
      return;
    }
    try {
      const updates = { [fieldKey]: newValue };
      const result = await updateAsset(originalAsset.assetUUID, updates);
      updateAssetInCache(result.asset || result);
      // Keep the rendered asset in sync so the re-render shows the new value
      // (merge, so joined data like assignedDevices stays intact).
      if (currentAsset) currentAsset = { ...currentAsset, [fieldKey]: newValue };
      window.showToast(`${fieldKey} updated`, 'success');
      exitEditMode(fieldKey, newValue);
    } catch (e) {
      window.showToast('Save failed', 'danger');
    }
  });

  // Cancel
  row.querySelector('.cancel-btn').addEventListener('click', () => {
    exitEditMode(fieldKey, originalValue);
  });
}

function exitEditMode(fieldKey, value) {
  // Re-render from the asset shown on this page. The old cache lookup broke
  // cancel/save whenever the asset wasn't in the assets-list cache (e.g.
  // direct navigation) — the click then simply did nothing.
  const assetUuid = document.getElementById('detail-header')?.dataset.assetUuid;
  const asset = (currentAsset && currentAsset.assetUUID === assetUuid ? currentAsset : null)
    || getAssetsCache().find(a => a.assetUUID === assetUuid);
  if (asset) {
    renderBasicFields(asset); // re-renders with correct values + re-attached listeners
  }
}


// Three-step breach-sensitivity slider values (index ↔ enum).
const BREACH_STEPS = ['near', 'medium', 'far'];

// Inline, DAU-proof help for every paired-device control — see docs/help-text.md.
// Two flavours share this dictionary (both wired by initHelp in wireSlotConfig):
//   • the "?" ICONS on the edit levers (helpIcon), and
//   • the read-only status PILLS (helpHover, `pill.*` keys) so a user WITHOUT
//     edit access still learns what each pill means.
// Each carries a short, concrete aviation example.
const HELP = {
  supervise:
    '<b>Supervise — is it still on the asset?</b><br>' +
    'Watches this device against its <i>home</i> (the tracker/gateway it should stay with). ' +
    'If home stops hearing it for several scans in a row you get a <b>“left the asset”</b> alert. ' +
    'A coarse <b>present / gone</b> check — it fires only once the device has effectively disappeared.' +
    '<br><br><b>Aviation:</b> A beacon on an engine bag rides on its engine stand. Someone carries the ' +
    'bag off to another hangar → the stand’s tracker no longer hears it → you’re alerted.' +
    '<br><br>Turn ON to watch this device at all; leave OFF for passive tags that need no alarm.',
  tether:
    '<b>Tether — has it started to drift away? (earlier warning)</b><br>' +
    'An optional upgrade to Supervise. Instead of waiting until the device is fully gone, it watches the ' +
    '<b>signal strength</b> to home and warns the moment it weakens past your chosen <i>breach distance</i> — ' +
    'the device is still faintly in range but has clearly moved.' +
    '<br><br><b>Supervise vs Tether:</b> Supervise = “it disappeared” (coarse, late). ' +
    'Tether = “it’s moving away” (graded, early). Tether needs Supervise ON (switching Tether on enables it), ' +
    'one <i>Set as docked</i> calibration and a breach distance.' +
    '<br><br><b>Aviation:</b> A tool trolley must never leave the aircraft — a handler sliding it 15 m across ' +
    'the ramp already warns you, long before it drops out of range.',
  home:
    '<b>Home gateway</b><br>' +
    'Which device is “home” — the anchor this beacon should stay next to. <b>Leave blank</b> and it anchors to ' +
    'the GPS tracker on <i>this same asset</i> (the normal case). <b>Enter an ID</b> only when home is a gateway ' +
    'on a <i>different</i> asset.' +
    '<br><br><b>Aviation:</b> The engine bag is tracked as its own asset but must stay by the engine stand — ' +
    'enter the stand’s tracker ID so the bag is measured against the stand, not against itself.',
  breach:
    '<b>Breach distance — how far may it drift before the tether alarm?</b><br>' +
    '<b>Near</b> ≈ a few metres (strict, most sensitive). <b>Medium</b> ≈ 10–20 m. ' +
    '<b>Far</b> ≈ nearly out of Bluetooth range (loose).' +
    '<br><br><b>Why only three steps, not metres?</b> Bluetooth signal strength maps only <i>roughly</i> to ' +
    'distance — a metal engine, a wall, or a person standing between the two devices changes it a lot. Exact ' +
    'metres would be false precision, so we offer three honest, reliable sensitivities instead.' +
    '<br><br><b>Aviation:</b> Near for a tool trolley that must hug the aircraft; Far for a bag allowed to ' +
    'wander the ramp.',
  dock:
    '<b>Set as docked — calibrate the baseline</b><br>' +
    'Press once while the beacon sits in its <b>normal home position</b>. It records the current signal strength ' +
    'as the “docked” 100% baseline; the breach distance is then measured as a <i>drop</i> from this reading. ' +
    'Without it the tether alarm has nothing to compare against.' +
    '<br><br>Re-dock if you move the beacon’s mounting or change its home gateway.' +
    '<br><br><b>Aviation:</b> With the engine bag resting on the stand, press Set as docked — that is “fully home”. ' +
    'A later signal drop then means it has moved.',
  dataOnly:
    '<b>Data only from home gateway</b><br>' +
    'Affects <b>history</b>, not alarms. By default <i>every</i> gateway that hears this beacon logs a point, so ' +
    'history is dense but the beacon appears to “jump” between gateways as different ones pick it up. Turn ON to ' +
    'log <b>only the home gateway’s</b> reads — a cleaner, calmer track at the cost of fewer points.' +
    '<br><br><b>Aviation:</b> On a busy apron a bag is heard by many tugs at once; Home-only keeps its trail tidy ' +
    'instead of teleporting between every passing gateway.',

  // ── read-only status PILLS (hover help; shown even without edit access) ──────
  'pill.gps':
    '<b>GPS tracker</b><br>' +
    'The asset’s own satellite tracker — it reports the position directly. The ' +
    'supervise/tether settings apply to <i>Bluetooth</i> tags, not to this device.',
  'pill.supervised':
    '<b>Supervised</b><br>' +
    'This Bluetooth device is watched against its home tracker. If home stops hearing it for several ' +
    'scans in a row you get a <b>“left the asset”</b> alert — a coarse present/gone check.' +
    '<br><br><b>Aviation:</b> a beacon on an engine bag — carried off to another hangar → you’re alerted.',
  'pill.rssi':
    '<b>Reception \u2014 how well is it heard?</b><br>' +
    'How strongly the tracker on this asset received this Bluetooth device in its last scan, '
    + 'as a step plus the raw value in dBm (less negative = stronger). '
    + '<b>strong</b> / <b>good</b> are comfortably in range; <b>weak</b> and <b>very weak</b> mean '
    + 'it is close to dropping out \u2014 the usual causes are distance, a metal mass in between, '
    + 'or a tired tag battery.'
    + '<br><br>It is the gateway\u2019s reading, not the tag\u2019s: a beacon has no signal figure of '
    + 'its own. Treat it as a range indicator, not a distance \u2014 Bluetooth strength maps only '
    + 'roughly to metres.'
    + '<br><br><b>Aviation:</b> a beacon on a cradle inside an engine stand reads <i>weak</i> while '
    + 'the engine is fitted and <i>good</i> once the stand is empty \u2014 the steel in between is what '
    + 'changed, not the distance.',
  'pill.tethered':
    '<b>Tethered</b><br>' +
    'An earlier, graded warning on top of Supervise: it watches the <b>signal strength</b> to home and warns ' +
    'as soon as the device drifts past the shown breach distance (<i>near / medium / far</i>) — while it’s ' +
    'still faintly in range.' +
    '<br><br><b>Aviation:</b> a tool trolley sliding 15 m across the ramp already warns you, long before it ' +
    'drops out of range.',
  'pill.docked':
    '<b>Docked @ … dBm</b><br>' +
    'The calibrated “fully home” signal baseline. The tether alarm measures a <i>drop</i> from this reading, ' +
    'so a weaker signal means the device has moved. Re-calibrate (<i>Set as docked</i>) after remounting.',
  'pill.notDocked':
    '<b>Not docked</b><br>' +
    'Tether is on but has <b>no baseline yet</b>, so it can’t raise a drift alarm. Open Edit and press ' +
    '<i>Set as docked</i> while the device sits in its normal home position.',
  'pill.homeGw':
    '<b>Home gateway</b><br>' +
    'This device is anchored to a specific gateway on a <i>different</i> asset (the shown ID), not to the ' +
    'tracker on its own asset.' +
    '<br><br><b>Aviation:</b> an engine bag tracked as its own asset but required to stay by the engine stand.',
  'pill.homeOnly':
    '<b>Home-only data</b><br>' +
    'Affects <b>history</b>, not alarms. Only the home gateway’s reads are logged, so the track stays tidy ' +
    'instead of jumping between every gateway that hears the device — at the cost of fewer points.',
  'pill.notSupervised':
    '<b>Not supervised</b><br>' +
    'This Bluetooth device is logged when heard but raises <b>no presence alerts</b>. Turn on Supervise ' +
    '(in Edit) to be warned if it leaves the asset.',
};

// "?" help icon → HELP[key], wired by initHelp in wireSlotConfig (ui/help.js).
const helpTip = helpIcon;

// A small CSV/XLSX/PDF download menu, shown only to users whose tenant holds the
// data-export add-on. Each item runs an ad-hoc report (same pipeline + entitlement
// + audit as the Reports page). Wired in wireExportMenus.
function exportMenu(id) {
  if (!canExport()) return '';
  return `
    <div class="btn-group btn-group-sm" role="group">
      <button type="button" class="btn btn-outline-primary dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false" id="${id}">
        <i class="fa-solid fa-download me-1"></i> Download
      </button>
      <ul class="dropdown-menu dropdown-menu-end">
        <li><a class="dropdown-item export-fmt" data-menu="${id}" data-fmt="csv"  href="#">CSV</a></li>
        <li><a class="dropdown-item export-fmt" data-menu="${id}" data-fmt="xlsx" href="#">Excel (XLSX)</a></li>
        <li><a class="dropdown-item export-fmt" data-menu="${id}" data-fmt="pdf"  href="#">PDF</a></li>
      </ul>
    </div>`;
}

// Wire the two inline export menus (sensor readings / raw uplinks). Each runs an
// ad-hoc report for THIS asset over the shown window and downloads the file —
// same async pipeline, entitlement check and audit trail as the Reports page.
function wireExportMenus(assetUUID, asset) {
  const activeRangeDays = () => {
    const r = document.querySelector('#detail-chart-ranges .btn-primary')?.dataset.range || '14d';
    return r === '24h' ? 1 : r === '7d' ? 7 : 14;
  };
  document.querySelectorAll('.export-fmt').forEach(item => {
    item.addEventListener('click', async (e) => {
      e.preventDefault();
      const isSensor = item.dataset.menu === 'sensor-export';
      const subject = isSensor ? 'sensorReadings' : 'rawUplinks';
      const days = isSensor ? activeRangeDays() : 14;
      const to = new Date();
      const from = new Date(to.getTime() - days * 86400000);
      const name = `${isSensor ? 'Sensor readings' : 'Raw uplinks'} — ${formatAssetLabel(asset)}`;
      window.showToast?.('Preparing download…', 'info');
      try {
        await quickExport({
          subject, assetUUID, format: item.dataset.fmt,
          from: from.toISOString(), to: to.toISOString(), name,
        });
        window.showToast?.('Download ready', 'success');
      } catch (err) {
        window.showToast?.(`Export failed: ${err.message}`, 'danger');
        console.error(err);
      }
    });
  });
}

// Read-only supervision/tether pills shown on the card body (no coordinates).
// Live sensor readout for a paired device's card — the values that make it a
// "sensor with a reading", not just a BLE tag: any derived (virtual) state
// (loaded/empty, ...), the Time-of-Flight distance, and temp/humidity when the
// tag reports them. Distances/temperatures honour the metric/imperial toggle.
// Griffin Air Ground Mode: "in flight" is only ever shown when the tracker
// reports it (transport template, Input 5). Trackers on older configurations
// never set it, so they show nothing here rather than a misleading "on ground".
function flightPill(d) {
  if (d.inFlight !== true) return '';
  const since = d.flightChangedAt ? ` since ${formatTimeAgo(d.flightChangedAt)}` : '';
  return `<span class="badge rounded-pill text-bg-info" title="The tracker detected take-off and has suspended cellular uploads until it detects landing. Buffered data arrives after the flight."><i class="fa-solid fa-plane me-1"></i>In flight${since}</span>`;
}

// A beacon is "present" while its gateway keeps reporting it. Once the gateway
// (the tracker on the same asset) has reported clearly later than the beacon's
// last sighting, the beacon is absent: it is off, out of range, or - for a
// beacon configured as a two-position switch (docs/tilt-sensing.md) - in its
// silent position. Two hours covers one missed hourly scan.
function beaconPresencePill(d, asset) {
  if (d.type !== 'bluetooth' || !d.lastSeen) return '';
  const gwId = d.gatewayDeviceId || d.posFromGateway;
  const gw = gwId ? (asset.assignedDevices || []).find(x => x.deviceId === gwId) : null;
  if (!gw?.lastSeen) return '';
  const gapMs = new Date(gw.lastSeen).getTime() - new Date(d.lastSeen).getTime();
  if (gapMs > 2 * 3600 * 1000) {
    return `<span class="badge rounded-pill text-bg-secondary" title="The tracker on this asset has reported ${formatTimeAgo(gw.lastSeen)} without seeing this beacon. Last sighting ${formatLocalTime(d.lastSeen)}."><i class="fa-solid fa-eye-slash me-1"></i>Absent ${formatTimeAgo(d.lastSeen)}</span>`;
  }
  return `<span class="badge rounded-pill text-bg-success" title="Seen by the tracker on this asset in its latest scan."><i class="fa-solid fa-eye me-1"></i>Present</span>`;
}

// How well the gateway hears this tag. A beacon has no signal figure of its own
// - the RSSI the gateway reported is the only one there is - and it is what says
// whether a tag sits comfortably in range or is about to drop out of it. It was
// only ever visible in the device detail modal, which is the wrong place for the
// one number that explains an Absent pill.
//
// Shown as a quality STEP plus the raw dBm: the steps are what an operator acts
// on, the number is what they quote to us. Deliberately coarse - Bluetooth
// signal strength maps only roughly to distance (same caveat as the tether
// breach steps), so a finer scale would promise precision that is not there.
const RSSI_STEPS = [
  { min: -60,       label: 'strong',    cls: 'text-bg-light text-dark border' },
  { min: -75,       label: 'good',      cls: 'text-bg-light text-dark border' },
  { min: -90,       label: 'weak',      cls: 'text-bg-warning' },
  { min: -Infinity, label: 'very weak', cls: 'text-bg-danger' },
];

function rssiPill(d) {
  if (d.type !== 'bluetooth' || typeof d.rssi !== 'number' || !Number.isFinite(d.rssi)) return '';
  const step = RSSI_STEPS.find(x => d.rssi >= x.min) || RSSI_STEPS[RSSI_STEPS.length - 1];
  return `<span class="badge rounded-pill ${step.cls}" ${helpHover('pill.rssi')}>`
    + `<i class="fa-solid fa-signal me-1"></i>${step.label} \u00b7 ${Math.round(d.rssi)} dBm</span>`;
}

function buildSensorPills(device) {
  const pills = [];
  const round = (n, d = 2) => { const f = 10 ** d; return Math.round(n * f) / f; };

  // Derived (virtual) sensors: any `${base}Label` whose base is a 0/1 number.
  // 1 = active → green, 0 = inactive → muted. Covers loadState/loadStateLabel etc.
  for (const key of Object.keys(device)) {
    if (!key.endsWith('Label')) continue;
    const base = key.slice(0, -'Label'.length);
    if (typeof device[base] !== 'number' || device[key] == null) continue;
    const on = device[base] === 1;
    pills.push(`<span class="badge rounded-pill ${on ? 'text-bg-success' : 'text-bg-secondary'}">${escapeHtml(String(device[key]))}</span>`);
  }

  // Time-of-Flight distance (BLE rangers store it under metadata). The Engine+
  // ToF spec range is 1-300 cm; at/above the 3 m ceiling nothing is in range, so
  // show "> 3 m" (no target) rather than a misleading exact/sentinel value.
  const TOF_MAX_M = 3;
  const distM = device.metadata?.distanceM ?? (device.metadata?.rangeMm != null ? device.metadata.rangeMm / 1000 : null);
  if (distM != null && Number.isFinite(Number(distM))) {
    const d = Number(distM);
    const u = displayUnit('m');
    pills.push(d >= TOF_MAX_M
      ? `<span class="badge rounded-pill text-bg-light text-dark border" title="Time-of-Flight: no target within range">&gt; ${round(convertNumber(TOF_MAX_M, 'm'))} ${u}</span>`
      : `<span class="badge rounded-pill text-bg-light text-dark border" title="Time-of-Flight distance">${round(convertNumber(d, 'm'))} ${u}</span>`);
  }

  // Environmental readings, only when the tag actually carries them (null skips).
  if (typeof device.temperature === 'number') {
    pills.push(`<span class="badge rounded-pill text-bg-light text-dark border" title="Temperature">${round(convertNumber(device.temperature, '°C'), 1)} ${displayUnit('°C')}</span>`);
  }
  if (typeof device.humidity === 'number') {
    pills.push(`<span class="badge rounded-pill text-bg-light text-dark border" title="Humidity">${Math.round(device.humidity)}%</span>`);
  }
  return pills.join(' ');
}

function buildSlotPills(slot, device) {
  const role = slot.role || deviceRole(device);
  const isBle = role === 'sensor' || role === 'beacon';
  // Every pill carries hover help (helpHover) — a read-only viewer without edit
  // access still learns what it means. Wired by initHelp in wireSlotConfig.
  if (!isBle) return `<span class="badge rounded-pill text-bg-light text-dark border" ${helpHover('pill.gps')}>GPS tracker</span>`;
  const pills = [];
  if (slot.supervise) pills.push(`<span class="badge rounded-pill text-bg-success" ${helpHover('pill.supervised')}>Supervised</span>`);
  if (slot.tethered) {
    pills.push(`<span class="badge rounded-pill text-bg-warning" ${helpHover('pill.tethered')}>Tethered · ${slot.breachDistance || 'medium'}</span>`);
    pills.push(slot.dockedRssi != null
      ? `<span class="badge rounded-pill text-bg-info" ${helpHover('pill.docked')}>Docked @ ${slot.dockedRssi} dBm</span>`
      : `<span class="badge rounded-pill text-bg-light text-dark border" ${helpHover('pill.notDocked')}>Not docked</span>`);
    if (slot.homeGatewayId) pills.push(`<span class="badge rounded-pill text-bg-light text-dark border" ${helpHover('pill.homeGw')}>Home GW: ${escapeHtml(slot.homeGatewayId)}</span>`);
  }
  if (slot.dataOnlyHome) pills.push(`<span class="badge rounded-pill text-bg-secondary" ${helpHover('pill.homeOnly')}>Home-only data</span>`);
  if (!pills.length) pills.push(`<span class="text-muted small" ${helpHover('pill.notSupervised')}>Not supervised</span>`);
  return pills.join(' ');
}

// Per-slot edit panel (hidden until the header Edit switch is on). Every change
// saves instantly; flipping Edit off — or reloading — returns to read-only.
function buildSlotConfig(slot, device) {
  const role = slot.role || deviceRole(device);
  const sid = slot.slotId;
  const fallback = slotDisplayLabel({ ...slot, label: '' }, device);
  const isBle = role === 'sensor' || role === 'beacon';

  // ── edit panel (hidden until Edit is on) ──
  const nameField = `
    <label class="form-label small mb-1 mt-2">Display name</label>
    <div class="input-group input-group-sm mb-2">
      <input class="form-control slot-label" data-slot="${sid}" value="${escapeHtml(slot.label || '')}" placeholder="${escapeHtml(fallback)}">
      <button class="btn btn-outline-secondary slot-label-save" data-slot="${sid}" title="Save name"><i class="fa-solid fa-check"></i></button>
    </div>`;

  let editBody = nameField;
  if (isBle) {
    const distIdx = Math.max(0, BREACH_STEPS.indexOf(slot.breachDistance || 'medium'));
    const dockInfo = slot.dockedRssi != null ? `<span class="text-muted small">docked @ ${slot.dockedRssi} dBm</span>` : '';
    editBody += `
      <div class="form-check form-switch small mb-1 d-flex align-items-center">
        <input class="form-check-input slot-toggle" type="checkbox" role="switch"
               data-slot="${sid}" data-field="supervise" id="sup-${sid}" ${slot.supervise ? 'checked' : ''}>
        <label class="form-check-label ms-2" for="sup-${sid}">Supervise — alert if it leaves the asset</label>
        ${helpTip('supervise')}
      </div>
      <div class="form-check form-switch small mb-1 d-flex align-items-center">
        <input class="form-check-input slot-toggle" type="checkbox" role="switch"
               data-slot="${sid}" data-field="tethered" id="tet-${sid}" ${slot.tethered ? 'checked' : ''}>
        <label class="form-check-label ms-2" for="tet-${sid}">Tether to a home gateway (RSSI breach)</label>
        ${helpTip('tether')}
      </div>
      <div class="slot-tether-extra ${slot.tethered ? '' : 'd-none'}" data-slot="${sid}">
        <label class="form-label small mb-1 mt-1">Home gateway device ID <span class="text-muted">(blank = tracker on this asset)</span>${helpTip('home')}</label>
        <input class="form-control form-control-sm slot-field mb-2" data-slot="${sid}" data-field="homeGatewayId"
               value="${escapeHtml(slot.homeGatewayId || '')}" placeholder="auto">
        <label class="form-label small mb-0 mt-1">Breach distance${helpTip('breach')}</label>
        <input type="range" min="0" max="2" step="1" value="${distIdx}" class="form-range slot-rssi" data-slot="${sid}">
        <div class="d-flex justify-content-between small text-muted" style="margin-top:-4px;">
          <span>Near</span><span>Medium</span><span>Far</span>
        </div>
        <div class="d-flex align-items-center gap-2 mt-1 mb-1">
          <button class="btn btn-outline-primary btn-sm slot-dock" data-slot="${sid}">Set as docked</button>
          ${helpTip('dock')}
          ${dockInfo}
        </div>
        <div class="form-check form-switch small mb-1 d-flex align-items-center">
          <input class="form-check-input slot-toggle" type="checkbox" role="switch"
                 data-slot="${sid}" data-field="dataOnlyHome" id="doh-${sid}" ${slot.dataOnlyHome ? 'checked' : ''}>
          <label class="form-check-label ms-2" for="doh-${sid}">Data only from home gateway</label>
          ${helpTip('dataOnly')}
        </div>
      </div>`;
  }

  return `<div class="slot-edit-panel d-none mt-2 pt-2 border-top" data-slot="${sid}">${editBody}</div>`;
}

// Attach listeners for the slot-config controls within the paired-devices area.
function wireSlotConfig(container, assetUUID) {
  const save = async (slotId, patch, ok = 'Saved') => {
    try {
      await updateAssetSlot(assetUUID, slotId, patch);
      const fresh = await getAssetDetail(assetUUID);
      if (fresh) updateAssetInCache(fresh);
      window.showToast?.(ok, 'success');
    } catch (e) {
      window.showToast?.('Slot update failed', 'danger');
      console.error(e);
    }
  };

  // Edit switch: reveal/hide the panel; turns red + labels "Editing" while on.
  // No server state — reloading the page returns to compact read-only.
  container.querySelectorAll('.slot-edit-toggle').forEach(sw => {
    sw.addEventListener('change', () => {
      const sid = sw.dataset.slot;
      const panel = container.querySelector(`.slot-edit-panel[data-slot="${sid}"]`);
      const label = sw.parentElement.querySelector('.form-check-label');
      panel?.classList.toggle('d-none', !sw.checked);
      sw.style.backgroundColor = sw.checked ? '#dc3545' : '';
      sw.style.borderColor = sw.checked ? '#dc3545' : '';
      if (label) {
        label.textContent = sw.checked ? 'Editing' : 'Edit';
        label.classList.toggle('text-danger', sw.checked);
        label.classList.toggle('text-muted', !sw.checked);
      }
    });
  });

  // Display name — also update the card title immediately (no reload needed).
  container.querySelectorAll('.slot-label-save').forEach(btn => {
    btn.addEventListener('click', async () => {
      const sid = btn.dataset.slot;
      const input = container.querySelector(`.slot-label[data-slot="${sid}"]`);
      const val = (input?.value || '').trim();
      await save(sid, { label: val }, 'Name saved');
      const titleEl = container.querySelector(`.slot-title[data-slot="${sid}"]`);
      if (titleEl) {
        const name = val || titleEl.dataset.fallback || '';
        titleEl.textContent = name;
        titleEl.title = name;
      }
    });
  });

  // Toggle switches (supervise / tethered / dataOnlyHome)
  container.querySelectorAll('.slot-toggle').forEach(sw => {
    sw.addEventListener('change', () => {
      const sid = sw.dataset.slot;
      const field = sw.dataset.field;
      if (field === 'tethered') {
        const extra = container.querySelector(`.slot-tether-extra[data-slot="${sid}"]`);
        extra?.classList.toggle('d-none', !sw.checked);
        // Tether only works while Supervise is on — the RSSI-drop breach is
        // evaluated for supervised slots only (utils/alerts/presence.mjs). So
        // enabling Tether enables Supervise too, in one save, and reflects it in
        // the switch. Otherwise Tether would silently do nothing.
        if (sw.checked) {
          const supSw = container.querySelector(`.slot-toggle[data-field="supervise"][data-slot="${sid}"]`);
          if (supSw && !supSw.checked) {
            supSw.checked = true;
            save(sid, { tethered: true, supervise: true }, 'Tether on (supervise enabled)');
            return;
          }
        }
      }
      save(sid, { [field]: sw.checked });
    });
  });

  // Wire ALL help in this area in one call — the "?" lever icons AND the
  // read-only status pills (so a user without edit access still learns what each
  // pill means). Always runs here, so it covers viewers too. See ui/help.js.
  initHelp(container, HELP);

  // Breach-distance slider (near/medium/far → breachDistance enum)
  container.querySelectorAll('.slot-rssi').forEach(rng => {
    rng.addEventListener('change', () => {
      const step = BREACH_STEPS[Number(rng.value)] || 'medium';
      save(rng.dataset.slot, { breachDistance: step }, `Breach distance: ${step}`);
    });
  });

  // Home gateway id text field (save on blur)
  container.querySelectorAll('.slot-field[data-field="homeGatewayId"]').forEach(inp => {
    inp.addEventListener('change', () => {
      save(inp.dataset.slot, { homeGatewayId: inp.value.trim() || null });
    });
  });

  // Set-as-docked calibration
  container.querySelectorAll('.slot-dock').forEach(btn => {
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      try {
        const res = await dockAssetSlot(assetUUID, btn.dataset.slot);
        const fresh = await getAssetDetail(assetUUID);
        if (fresh) updateAssetInCache(fresh);
        window.showToast?.(`Calibrated docked RSSI: ${res.dockedRssi} dBm`, 'success');
        await renderAssetDetail(document.querySelector('#main-content') || document.getElementById('root'), assetUUID, currentOnBack);
      } catch (e) {
        window.showToast?.(e.message || 'Calibration failed', 'danger');
        btn.disabled = false;
      }
    });
  });
}

async function renderPairedDevicesSection(asset, assetUUID) {
  const container = document.getElementById('paired-devices-section');
  const countBadge = document.getElementById('device-count-badge');

  // Sort (a copy, so the cached asset stays untouched): active trackers first
  // (most-recently-seen on top), then the sensors GROUPED BY TYPE so identical
  // sensors sit together. Stable by deviceId within a type.
  const seen = (d) => new Date(d?.lastSeen || 0).getTime() || 0;
  const isTracker = (d) => {
    const role = slotForDevice(asset, d.deviceId)?.role || deviceRole(d);
    return role === 'tracker' || d.type === 'digitalmatter';
  };
  const typeKey = (d) => deviceTypeLabel(d) || d.subType || d.type || '';
  const assignedDevices = [...(asset.assignedDevices || [])].sort((a, b) => {
    const ta = isTracker(a), tb = isTracker(b);
    if (ta !== tb) return ta ? -1 : 1;                 // trackers first
    if (ta) return seen(b) - seen(a);                  // trackers: most recent first
    const ka = typeKey(a), kb = typeKey(b);
    if (ka !== kb) return ka.localeCompare(kb);        // sensors grouped by type
    return String(a.deviceId).localeCompare(String(b.deviceId));
  });

  countBadge.textContent = assignedDevices.length;

  let html = `<div class="d-flex flex-wrap gap-3 mb-4" id="current-devices-list"></div>`;

  if (assignedDevices.length > 0) {
    html += assignedDevices.map(d => {
      // Client-locale relative time + full local time on hover (timezone-correct).
      const lastSeen = d.lastSeen ? formatTimeAgo(d.lastSeen) : '—';
      const lastSeenFull = d.lastSeen ? formatLocalTime(d.lastSeen, { second: '2-digit' }) : '';


      const serialInfo = d.serialNumber || d.imei || '—';

      // Type-aware accent + icon so each device stands out as its own card.
      const isBle = d.type === 'bluetooth';
      const icon = isBle ? 'fa-bluetooth-b' : 'fa-satellite-dish';
      const accent = isBle ? '#0ea5e9' : '#6366f1';
      const sub = d.subType || (d.metadata && d.metadata.subType) || d.type || 'device';

      // The logical slot this device occupies → display name + supervision config.
      const slot = slotForDevice(asset, d.deviceId);
      const dispName = slotDisplayLabel(slot, d);
      const fallback = slotDisplayLabel({ ...(slot || {}), label: '' }, d);

      // Real product photo when we have one, else the type icon.
      const imgSrc = deviceImage(d, 'small');
      const avatar = imgSrc
        ? `<img src="${imgSrc}" alt="" style="width:40px;height:40px;object-fit:contain;flex:0 0 auto;" onerror="this.outerHTML='<span style=\\'display:inline-flex;width:40px;height:40px;border-radius:50%;background:${accent}22;color:${accent};align-items:center;justify-content:center\\'><i class=\\'fa-solid ${icon}\\'></i></span>'">`
        : `<span style="display:inline-flex;width:40px;height:40px;border-radius:50%;background:${accent}22;color:${accent};align-items:center;justify-content:center;flex:0 0 auto;"><i class="fa-solid ${icon}"></i></span>`;

      const editSwitch = slot ? `
        <div class="form-check form-switch m-0">
          <input class="form-check-input slot-edit-toggle" type="checkbox" role="switch" data-slot="${slot.slotId}" id="edit-${slot.slotId}">
          <label class="form-check-label small text-muted" for="edit-${slot.slotId}">Edit</label>
        </div>` : '';

      return `
        <div class="card shadow-sm" style="min-width:300px;border-left:4px solid ${accent};">
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-start gap-2">
              <div class="d-flex align-items-center gap-2" style="min-width:0;">
                ${avatar}
                <div style="min-width:0;">
                  <strong class="slot-title d-block text-truncate" data-slot="${slot ? slot.slotId : ''}" data-fallback="${escapeHtml(fallback)}" title="${escapeHtml(dispName)}">${escapeHtml(dispName)}</strong>
                  <small class="text-muted text-truncate d-block" title="${escapeHtml(d.deviceId)}">${escapeHtml(d.deviceId)} · ${escapeHtml(serialInfo)}</small>
                </div>
              </div>
              <div class="d-flex align-items-center gap-2 flex-shrink-0">
                ${editSwitch}
                <button class="btn btn-sm btn-outline-danger p-1 lh-1 remove-device-btn" data-device-id="${escapeHtml(d.deviceId)}" data-device-name="${escapeHtml(dispName)}" title="Unassign device" style="width:30px;height:30px;">
                  <i class="fa-solid fa-trash-can"></i>
                </button>
              </div>
            </div>
            <div class="d-flex flex-wrap gap-2 mt-3 small align-items-center">
              ${(() => {
                const href = manualHrefForDevice(d), label = deviceTypeLabel(d);
                return href
                  ? `<a href="${href}" class="badge text-decoration-none manual-link" style="background:${accent};" title="Open in the device manual">${escapeHtml(label)} <i class="fa-solid fa-book ms-1"></i></a>`
                  : `<span class="badge" style="background:${accent};">${escapeHtml(label)}</span>`;
              })()}
              ${/* Battery: the remaining-life estimate only (2026-09-14). The raw
                    voltage says almost nothing on these packs; it lives in the
                    device detail modal for whoever needs it. */ ''}
              ${batteryRemainingPill(d)}
              ${buildSensorPills(d)}
              ${flightPill(d)}
              ${beaconPresencePill(d, asset)}
              ${rssiPill(d)}
              <span class="text-muted" title="${lastSeenFull}">Seen: ${lastSeen}</span>
            </div>
            ${slot ? `<div class="d-flex flex-wrap gap-1 mt-2">${buildSlotPills(slot, d)}</div>` : ''}
            ${slot ? buildSlotConfig(slot, d) : ''}
          </div>
        </div>`;
    }).join('');
  } else {
    html += `<div class="text-muted small p-3 border border-dashed rounded">No devices assigned yet.</div>`;
  }

  // Add-device (select2) — collapsed by default, since a paired asset rarely
  // needs new devices attached; keeps the day-to-day view compact.
  html += `
    <div class="mt-4">
      <button type="button" class="btn btn-sm btn-outline-secondary collapsed" data-bs-toggle="collapse"
              data-bs-target="#add-device-collapse" aria-expanded="false" aria-controls="add-device-collapse">
        <i class="fa-solid fa-plus me-1"></i>Add device
      </button>
      <div class="collapse mt-3" id="add-device-collapse">
        <label class="form-label fw-bold">Add unassigned devices (search by ID, serial, IMEI...)</label>
        <select id="paired-device-select2" class="form-select" multiple style="width:100%"></select>
        <button id="btn-save-paired-devices" class="btn btn-success w-100 mt-3">Save Device Assignments</button>
      </div>
    </div>
  `;

  container.innerHTML = html;

  // Battery pills use the same help mechanism as the slot pills below; their
  // copy is per device, so the dictionary comes from the rows just rendered.
  wireBatteryHelp(container, assignedDevices);

  // Slot config listeners (display name, supervise/tether switches, dock).
  wireSlotConfig(container, assetUUID);

  // Select2 + remove listener (unchanged)
  try {
    const unassigned = await getUnassignedDevices();
    const $select = $('#paired-device-select2');
    $select.select2({
      placeholder: 'Search by deviceId, serial, IMEI, type…',
      data: unassigned.map(d => {
        const sn = d.serialNumber || d.imei || d.SerNo || 'N/A';
        return {
          id: d.deviceId,
          // Richer identification: model/category · id · serial · last seen.
          text: `${deviceTypeLabel(d)} · ${d.deviceId} · SN ${sn}${d.lastSeen ? ' · seen ' + new Date(d.lastSeen).toLocaleDateString() : ''}`,
          device: d,
        };
      }),
      allowClear: true,
      width: '100%'
    });
  } catch (e) {
    console.error('Failed to load unassigned devices', e);
  }

  // Remove (styled confirm instead of native prompt)
  container.querySelectorAll('.remove-device-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const devId = btn.dataset.deviceId;
      const name = btn.dataset.deviceName || devId;
      const ok = await showConfirm({
        title: 'Unassign device?',
        body: `<p><strong>${name}</strong> <span class="text-muted">(${devId})</span> will be detached from this asset.</p>
               <p class="small text-muted mb-0">Its slot (name + history) is kept, so re-pairing the same or a replacement device restores continuity.</p>`,
        confirmText: 'Unassign',
        variant: 'danger',
        icon: 'fa-link-slash',
      });
      if (!ok) return;
      const currentIds = assignedDevices.map(d => d.deviceId).filter(id => id !== devId);
      await savePairedDevices(assetUUID, currentIds);
    });
  });

  // Save
  document.getElementById('btn-save-paired-devices').onclick = async () => {
    const $select = $('#paired-device-select2');
    const selectedIds = $select.val() || [];
    const currentIds = assignedDevices.map(d => d.deviceId);
    const finalIds = [...new Set([...currentIds, ...selectedIds])];
    await savePairedDevices(assetUUID, finalIds);
  };
}

async function savePairedDevices(assetUUID, deviceIds) {
  try {
    const result = await updateAsset(assetUUID, { assignedDeviceIds: deviceIds });
    updateAssetInCache(result.asset || result);
    window.showToast('Device assignments updated', 'success');
    // Refresh detail view
    const container = document.querySelector('#main-content') || document.getElementById('root');
    await renderAssetDetail(container, assetUUID, currentOnBack);
  } catch (e) {
    window.showToast('Failed to update devices', 'danger');
    console.error(e);
  }
}